import {
    parseMagicMd as parseMagicMdDocument,
    type ParsedDocument,
} from "@magic-md/core";
import type { StateCreator } from "zustand";
import {
    type EditorState,
    type EditorTab,
    type PaneLayout,
    type PaneSlice,
    type PaneStateEntry,
    type PreviewWidth,
    type ViewMode,
} from "./types";
import { isMarkdownFile } from "../../lib/file-type";
import {
    isPersistablePreviewWidthSetting,
    normalizePreviewWidth,
} from "../../lib/preview-width";
import { scheduleSessionPersist, scheduleTabActivationParse } from "./tabs-slice";
import {
    countExpandedPaneLeaves,
    getPaneLayoutLeafIds,
    getPaneLayoutNodeAtPath,
    getSidebarPresentation,
    isPaneCollapsedInLayout,
    removePaneFromLayout,
    persistEditorUserSetting,
    setPaneCollapsedInLayout,
    setPaneLayoutNodeCollapsedInLayout,
    splitPercentForViewMode,
    viewModeForSplitPercent,
} from "./utils";

function parseTabContent(content: string, filePath?: string): ParsedDocument | null {
    if (!isMarkdownFile(filePath)) return null;
    return parseMagicMdDocument(content);
}

export const createPaneSlice: StateCreator<EditorState, [], [], PaneSlice> = (
    set,
    get,
) => ({
    /* Multi-Pane: single default pane initially */
    panes: [],
    focusedPaneId: "pane-main",
    paneLayout: null,

    /* ── Multi-Pane Management ── */
    setFocusedPane: (paneId: string) => {
        const { panes, focusedPaneId, parsedDocumentMap } = get();
        if (paneId === focusedPaneId) return;
        const pane = panes.find((p) => p.paneId === paneId);
        if (pane) {
            // Mirror the newly-focused pane's view mode into the global
            // `viewMode`/`splitPercent` fields so back-compat selectors
            // (Toolbar, TocPanel, PaneContext) stay in sync.
            set({
                focusedPaneId: paneId,
                activeTabId: pane.tabId,
                parsedDocument: parsedDocumentMap.get(pane.tabId) ?? null,
                viewMode: pane.viewMode,
                splitPercent: pane.splitPercent,
            });
        } else {
            set({ focusedPaneId: paneId });
        }
        if (pane && !parsedDocumentMap.has(pane.tabId)) {
            scheduleTabActivationParse(pane.tabId, get, set);
        }
        scheduleSessionPersist(get);
    },

    setPaneViewMode: (paneId: string, mode: ViewMode) => {
        const pct = splitPercentForViewMode(mode);
        set((s) => {
            const pane = s.panes.find((p) => p.paneId === paneId);
            if (pane) {
                const nextPanes = s.panes.map((p) =>
                    p.paneId === paneId
                        ? { ...p, viewMode: mode, splitPercent: pct }
                        : p,
                );
                // Mirror to global when we just touched the focused pane.
                if (paneId === s.focusedPaneId) {
                    return { panes: nextPanes, viewMode: mode, splitPercent: pct };
                }
                return { panes: nextPanes };
            }
            // Single-pane fallback: caller passed a paneId that doesn't
            // exist (typically "pane-main"). Treat as global update.
            return { viewMode: mode, splitPercent: pct };
        });
        scheduleSessionPersist(get);
    },

    setPaneSplitPercent: (paneId: string, pct: number) => {
        const clamped = Math.max(0, Math.min(100, pct));
        const viewMode = viewModeForSplitPercent(clamped);
        set((s) => {
            const pane = s.panes.find((p) => p.paneId === paneId);
            if (pane) {
                const nextPanes = s.panes.map((p) =>
                    p.paneId === paneId ? { ...p, splitPercent: clamped, viewMode } : p,
                );
                if (paneId === s.focusedPaneId) {
                    return { panes: nextPanes, splitPercent: clamped, viewMode };
                }
                return { panes: nextPanes };
            }
            return { splitPercent: clamped, viewMode };
        });
        scheduleSessionPersist(get);
    },

    setPanePreviewWidth: (paneId: string, width: PreviewWidth) => {
        const nextWidth = normalizePreviewWidth(width);
        set((s) => {
            const pane = s.panes.find((p) => p.paneId === paneId);
            if (pane) {
                return {
                    panes: s.panes.map((p) =>
                        p.paneId === paneId ? { ...p, previewWidth: nextWidth } : p,
                    ),
                };
            }
            return { previewWidth: nextWidth };
        });
        if (get().panes.length === 0 && isPersistablePreviewWidthSetting(nextWidth)) {
            void persistEditorUserSetting("editor.defaultPreviewWidth", nextWidth);
        }
        scheduleSessionPersist(get);
    },

    setPaneCursorPosition: (paneId: string, line: number, col: number) => {
        set((s) => {
            const pane = s.panes.find((p) => p.paneId === paneId);
            if (pane) {
                if (pane.cursorLine === line && pane.cursorCol === col) {
                    return s;
                }
                return {
                    panes: s.panes.map((p) =>
                        p.paneId === paneId
                            ? { ...p, cursorLine: line, cursorCol: col }
                            : p,
                    ),
                };
            }
            if (s.cursorLine === line && s.cursorCol === col) {
                return s;
            }
            return { cursorLine: line, cursorCol: col };
        });
    },

    getFocusedTabId: () => {
        const { panes, focusedPaneId, activeTabId } = get();
        const pane = panes.find((p) => p.paneId === focusedPaneId);
        return pane?.tabId ?? activeTabId;
    },

    /* ── Pane Layout (Phase 3) ── */

    splitPane: (direction, newTabId?) => {
        const { panes, focusedPaneId, activeTabId, tabs, paneLayout } = get();

        let targetTabId = newTabId ?? "";
        if (!targetTabId) {
            // Create empty tab
            const emptyTabId = `tab-${Date.now()}`;
            const emptyTab: EditorTab = {
                id: emptyTabId,
                title: "Untitled.md",
                content: "",
                isDirty: false,
            };
            const parsed = parseTabContent("", emptyTab.filePath);
            set((s) => {
                const nextMap = new Map(s.parsedDocumentMap);
                nextMap.set(emptyTabId, parsed);
                return {
                    tabs: [...s.tabs, emptyTab],
                    parsedDocumentMap: nextMap,
                };
            });
            targetTabId = emptyTabId;
        }
        if (!get().tabs.some((t) => t.id === targetTabId)) return;

        const newPaneId = `pane-${Date.now()}`;

        const newPane: PaneStateEntry = {
            paneId: newPaneId,
            tabId: targetTabId,
            tabIds: [targetTabId],
            viewMode: get().defaultViewMode,
            splitPercent: splitPercentForViewMode(get().defaultViewMode),
            previewWidth: get().previewWidth,
            cursorLine: 1,
            cursorCol: 1,
        };

        let existingPanes = panes;
        let baseLayout = paneLayout;
        if (existingPanes.length === 0) {
            const mainPane: PaneStateEntry = {
                paneId: "pane-main",
                tabId: activeTabId,
                tabIds: tabs.map((t) => t.id),
                viewMode: get().viewMode,
                splitPercent: get().splitPercent,
                previewWidth: get().previewWidth,
                cursorLine: get().cursorLine,
                cursorCol: get().cursorCol,
            };
            existingPanes = [mainPane];
            baseLayout = { type: "leaf", paneId: "pane-main" };
        }

        const currentPaneId = existingPanes.some((p) => p.paneId === focusedPaneId)
            ? focusedPaneId
            : existingPanes[0].paneId;

        const insertSplit = (node: PaneLayout): PaneLayout => {
            if (node.type === "leaf" && node.paneId === currentPaneId) {
                return {
                    type: direction,
                    children: [node, { type: "leaf", paneId: newPaneId }],
                    sizes: [50, 50],
                };
            }
            if (node.type !== "leaf") {
                return {
                    ...node,
                    children: node.children.map(insertSplit),
                } as PaneLayout;
            }
            return node;
        };

        const newLayout = baseLayout
            ? insertSplit(baseLayout)
            : {
                type: direction as "horizontal" | "vertical",
                children: [
                    { type: "leaf" as const, paneId: currentPaneId },
                    { type: "leaf" as const, paneId: newPaneId },
                ],
                sizes: [50, 50],
            };

        set({
            panes: [...existingPanes, newPane],
            paneLayout: newLayout,
            focusedPaneId: newPaneId,
            activeTabId: targetTabId,
            parsedDocument: get().parsedDocumentMap.get(targetTabId) ?? null,
        });

        const { parsedDocumentMap } = get();
        if (!parsedDocumentMap.has(targetTabId)) {
            const tab = get().tabs.find((t) => t.id === targetTabId);
            if (tab) {
                if (isMarkdownFile(tab.filePath)) {
                    scheduleTabActivationParse(targetTabId, get, set);
                } else {
                    const doc = parseTabContent(tab.content, tab.filePath);
                    const next = new Map(parsedDocumentMap);
                    next.set(targetTabId, doc);
                    set({ parsedDocumentMap: next });
                }
            }
        }
        scheduleSessionPersist(get);
    },

    closePane: (paneId: string) => {
        const { panes, paneLayout, focusedPaneId } = get();
        if (panes.length <= 1) {
            set({ panes: [], paneLayout: null, focusedPaneId: "pane-main" });
            scheduleSessionPersist(get);
            return;
        }

        const remaining = panes.filter((p) => p.paneId !== paneId);

        const newLayout = paneLayout
            ? removePaneFromLayout(paneLayout, paneId)
            : null;

        const newFocusedId =
            focusedPaneId === paneId ? remaining[0].paneId : focusedPaneId;

        const newActiveTabId =
            remaining.find((p) => p.paneId === newFocusedId)?.tabId ??
            get().activeTabId;

        set({
            panes: remaining,
            paneLayout:
                newLayout?.type === "leaf" && remaining.length === 1 ? null : newLayout,
            focusedPaneId: newFocusedId,
            activeTabId: newActiveTabId,
            parsedDocument: get().parsedDocumentMap.get(newActiveTabId) ?? null,
        });
        scheduleSessionPersist(get);
    },

    setPaneCollapsed: (paneId: string, collapsed: boolean) => {
        const { panes, paneLayout, focusedPaneId, parsedDocumentMap } = get();
        if (!paneLayout || panes.length <= 1) return;
        const pane = panes.find((p) => p.paneId === paneId);
        if (!pane) return;

        const currentlyCollapsed = isPaneCollapsedInLayout(paneLayout, paneId);
        if (currentlyCollapsed === collapsed) return;
        if (collapsed && countExpandedPaneLeaves(paneLayout) <= 1) return;

        const nextLayout = setPaneCollapsedInLayout(paneLayout, paneId, collapsed);
        const patch: Partial<EditorState> = { paneLayout: nextLayout };

        if (!collapsed) {
            patch.focusedPaneId = paneId;
            patch.activeTabId = pane.tabId;
            patch.parsedDocument = parsedDocumentMap.get(pane.tabId) ?? null;
            patch.viewMode = pane.viewMode;
            patch.splitPercent = pane.splitPercent;
        } else if (focusedPaneId === paneId) {
            const allPaneIds = getPaneLayoutLeafIds(paneLayout, {
                includeCollapsed: true,
            });
            const expandedPaneIds = getPaneLayoutLeafIds(nextLayout);
            const currentIndex = allPaneIds.indexOf(paneId);
            const fallbackPaneId =
                expandedPaneIds.find((id) => allPaneIds.indexOf(id) > currentIndex) ??
                [...expandedPaneIds]
                    .reverse()
                    .find((id) => allPaneIds.indexOf(id) < currentIndex) ??
                expandedPaneIds[0];
            const fallbackPane = panes.find((p) => p.paneId === fallbackPaneId);
            if (fallbackPane) {
                patch.focusedPaneId = fallbackPane.paneId;
                patch.activeTabId = fallbackPane.tabId;
                patch.parsedDocument =
                    parsedDocumentMap.get(fallbackPane.tabId) ?? null;
                patch.viewMode = fallbackPane.viewMode;
                patch.splitPercent = fallbackPane.splitPercent;
            }
        }

        set(patch);
        scheduleSessionPersist(get);
    },

    setPaneLayoutNodeCollapsed: (path: number[], collapsed: boolean) => {
        const { panes, paneLayout, focusedPaneId, parsedDocumentMap } = get();
        if (!paneLayout || panes.length <= 1) return;

        const targetNode = getPaneLayoutNodeAtPath(paneLayout, path);
        if (!targetNode) return;
        if (targetNode.type === "leaf") {
            get().setPaneCollapsed(targetNode.paneId, collapsed);
            return;
        }

        const currentlyCollapsed = targetNode.collapsed === true;
        if (currentlyCollapsed === collapsed) return;

        const targetExpandedCount = countExpandedPaneLeaves(targetNode);
        if (collapsed && targetExpandedCount === 0) return;
        if (
            collapsed &&
            countExpandedPaneLeaves(paneLayout) - targetExpandedCount <= 0
        ) {
            return;
        }

        const targetPaneIds = new Set(
            getPaneLayoutLeafIds(targetNode, { includeCollapsed: true }),
        );
        const nextLayout = setPaneLayoutNodeCollapsedInLayout(
            paneLayout,
            path,
            collapsed,
        );
        const patch: Partial<EditorState> = { paneLayout: nextLayout };

        if (!collapsed) {
            const expandedTargetPaneIds = getPaneLayoutLeafIds(
                getPaneLayoutNodeAtPath(nextLayout, path),
            );
            const fallbackPaneId = expandedTargetPaneIds[0];
            const fallbackPane = panes.find((p) => p.paneId === fallbackPaneId);
            if (fallbackPane) {
                patch.focusedPaneId = fallbackPane.paneId;
                patch.activeTabId = fallbackPane.tabId;
                patch.parsedDocument =
                    parsedDocumentMap.get(fallbackPane.tabId) ?? null;
                patch.viewMode = fallbackPane.viewMode;
                patch.splitPercent = fallbackPane.splitPercent;
            }
        } else if (targetPaneIds.has(focusedPaneId)) {
            const allPaneIds = getPaneLayoutLeafIds(paneLayout, {
                includeCollapsed: true,
            });
            const expandedPaneIds = getPaneLayoutLeafIds(nextLayout);
            const currentIndex = allPaneIds.indexOf(focusedPaneId);
            const fallbackPaneId =
                expandedPaneIds.find((id) => allPaneIds.indexOf(id) > currentIndex) ??
                [...expandedPaneIds]
                    .reverse()
                    .find((id) => allPaneIds.indexOf(id) < currentIndex) ??
                expandedPaneIds[0];
            const fallbackPane = panes.find((p) => p.paneId === fallbackPaneId);
            if (fallbackPane) {
                patch.focusedPaneId = fallbackPane.paneId;
                patch.activeTabId = fallbackPane.tabId;
                patch.parsedDocument =
                    parsedDocumentMap.get(fallbackPane.tabId) ?? null;
                patch.viewMode = fallbackPane.viewMode;
                patch.splitPercent = fallbackPane.splitPercent;
            }
        }

        set(patch);
        scheduleSessionPersist(get);
    },

    togglePaneCollapsed: (paneId: string) => {
        const { paneLayout } = get();
        if (!paneLayout) return;
        get().setPaneCollapsed(
            paneId,
            !isPaneCollapsedInLayout(paneLayout, paneId),
        );
    },

    setPaneLayoutSizes: (path: number[], sizes: number[]) => {
        const { paneLayout } = get();
        if (!paneLayout) return;

        const updateSizes = (node: PaneLayout, depth: number): PaneLayout => {
            if (depth >= path.length) {
                if (node.type !== "leaf") {
                    return { ...node, sizes } as PaneLayout;
                }
                return node;
            }
            if (node.type === "leaf") return node;
            return {
                ...node,
                children: node.children.map((child, i) =>
                    i === path[depth] ? updateSizes(child, depth + 1) : child,
                ),
            } as PaneLayout;
        };

        set({ paneLayout: updateSizes(paneLayout, 0) });
        scheduleSessionPersist(get);
    },

    setPaneTab: (paneId: string, tabId: string) => {
        const { panes, tabs, parsedDocumentMap } = get();
        if (!tabs.some((t) => t.id === tabId)) return;
        const nextPanes = panes.map((p) => {
            if (p.paneId !== paneId) return p;
            const tabIds = p.tabIds.includes(tabId) ? p.tabIds : [...p.tabIds, tabId];
            return { ...p, tabId, tabIds };
        });
        const hasCachedParsed = parsedDocumentMap.has(tabId);
        let parsed = hasCachedParsed ? (parsedDocumentMap.get(tabId) ?? null) : null;
        let nextParsedDocumentMap = parsedDocumentMap;
        let shouldScheduleActivationParse = false;
        if (!hasCachedParsed) {
            const tab = tabs.find((t) => t.id === tabId);
            if (tab) {
                if (isMarkdownFile(tab.filePath)) {
                    shouldScheduleActivationParse = true;
                } else {
                    parsed = parseTabContent(tab.content, tab.filePath);
                    nextParsedDocumentMap = new Map(parsedDocumentMap);
                    nextParsedDocumentMap.set(tabId, parsed);
                }
            }
        }
        const update: Partial<EditorState> = {
            panes: nextPanes,
            focusedPaneId: paneId,
            activeTabId: tabId,
            parsedDocument: parsed,
            sidebarOpen:
                get().sidebarOpen && getSidebarPresentation(get().sidebarView) === "content"
                    ? false
                    : get().sidebarOpen,
        };
        if (nextParsedDocumentMap !== parsedDocumentMap) {
            update.parsedDocumentMap = nextParsedDocumentMap;
        }
        set(update);
        if (shouldScheduleActivationParse) {
            scheduleTabActivationParse(tabId, get, set);
        }
        scheduleSessionPersist(get);
    },

    removeTabFromPane: (paneId: string, tabId: string) => {
        const { panes } = get();
        const nextPanes = panes.map((p) => {
            if (p.paneId !== paneId) return p;
            const filteredIds = p.tabIds.filter((tid) => tid !== tabId);
            if (filteredIds.length === p.tabIds.length) return p;
            const newActiveTab =
                p.tabId === tabId
                    ? filteredIds.length > 0
                        ? filteredIds[
                        Math.min(p.tabIds.indexOf(tabId), filteredIds.length - 1)
                        ]
                        : ""
                    : p.tabId;
            return { ...p, tabIds: filteredIds, tabId: newActiveTab };
        });
        set({ panes: nextPanes });
        scheduleSessionPersist(get);
    },

    reorderPaneTabs: (paneId: string, tabIds: string[]) => {
        const { panes } = get();
        const nextPanes = panes.map((p) => {
            if (p.paneId !== paneId) return p;
            return { ...p, tabIds };
        });
        set({ panes: nextPanes });
        scheduleSessionPersist(get);
    },
});
